"use client";

import { api } from "@convex/_generated/api";
import { useMutation, useQuery } from "convex/react";
import { Radio, Settings } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { UserProfile } from "../utils";

type ProfileSettingsDialogProps = {
  profile: UserProfile;
};

export function ProfileSettingsDialog({ profile }: ProfileSettingsDialogProps) {
  const [open, setOpen] = useState(false);
  const [twitchUsername, setTwitchUsername] = useState(
    profile.twitchUsername || ""
  );
  const [youtubeChannelId, setYoutubeChannelId] = useState(
    profile.youtubeChannelId || ""
  );
  const [twitterHandle, setTwitterHandle] = useState(
    profile.twitterHandle || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const twitchInputRef = useRef<HTMLInputElement>(null);

  const currentUser = useQuery(api.users.queries.current);
  const updateSocialLinks = useMutation(api.users.mutations.updateSocialLinks);

  // Only show settings button if viewing own profile
  const isOwnProfile = currentUser?._id === profile._id;

  // Check for #settings hash in URL to auto-open dialog
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (window.location.hash === "#settings") {
      setOpen(true);
      // Focus the Twitch input after dialog opens
      setTimeout(() => {
        twitchInputRef.current?.focus();
      }, 100);
      // Clear the hash
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  if (!isOwnProfile) {
    return null;
  }

  const hasStreamConnected = profile.twitchUsername || profile.youtubeChannelId;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      await updateSocialLinks({
        twitchUsername: twitchUsername.trim() || undefined,
        youtubeChannelId: youtubeChannelId.trim() || undefined,
        twitterHandle: twitterHandle.trim() || undefined,
      });

      setSuccessMessage("Social links updated successfully!");

      // Close dialog after 1.5 seconds
      const CLOSE_DIALOG_DELAY = 1500;
      setTimeout(() => {
        setOpen(false);
        setSuccessMessage(null);
      }, CLOSE_DIALOG_DELAY);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update social links"
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button size="sm" variant={hasStreamConnected ? "outline" : "primary"}>
          {hasStreamConnected ? (
            <>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </>
          ) : (
            <>
              <Radio className="mr-2 h-4 w-4" />
              Connect Stream
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {hasStreamConnected ? "Profile Settings" : "🎮 Connect Your Stream"}
          </DialogTitle>
          <DialogDescription>
            {hasStreamConnected
              ? "Update your streaming and social links. These will be displayed on your profile and can help viewers find your content."
              : "Add your Twitch or YouTube channel to automatically display your live stream on your profile and all your content. Grow your audience while sharing your tactical expertise!"}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Twitch Username */}
            <div className="space-y-2">
              <Label className="font-semibold text-base" htmlFor="twitch">
                🟣 Twitch Username
                {!hasStreamConnected && (
                  <span className="ml-2 rounded bg-primary/10 px-2 py-0.5 font-normal text-primary text-xs">
                    Recommended
                  </span>
                )}
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">twitch.tv/</span>
                <Input
                  id="twitch"
                  onChange={(e) => setTwitchUsername(e.target.value)}
                  placeholder="your_username"
                  ref={twitchInputRef}
                  type="text"
                  value={twitchUsername}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                ✨ When you're live, your stream will automatically appear on
                your profile, loadouts, and weapon builds
              </p>
            </div>

            {/* YouTube Channel ID */}
            <div className="space-y-2">
              <Label htmlFor="youtube">
                YouTube Channel ID
                <span className="ml-2 text-muted-foreground text-sm">
                  (optional)
                </span>
              </Label>
              <Input
                id="youtube"
                onChange={(e) => setYoutubeChannelId(e.target.value)}
                placeholder="UC..."
                type="text"
                value={youtubeChannelId}
              />
              <p className="text-muted-foreground text-xs">
                Find your channel ID in YouTube Studio → Settings → Channel →
                Advanced
              </p>
            </div>

            {/* Twitter Handle */}
            <div className="space-y-2">
              <Label htmlFor="twitter">
                Twitter Handle
                <span className="ml-2 text-muted-foreground text-sm">
                  (optional)
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">@</span>
                <Input
                  id="twitter"
                  onChange={(e) => setTwitterHandle(e.target.value)}
                  placeholder="username"
                  type="text"
                  value={twitterHandle}
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="rounded-md bg-green-500/10 p-3 text-green-600 text-sm dark:text-green-400">
              {successMessage}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              disabled={isSaving}
              onClick={() => setOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isSaving} type="submit">
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
